import { createContext, ReactElement, useContext, useMemo, useState } from 'react'
import { noop, BannerProps, BannerAppearance } from '@panwds/react-ui'
import MessageBanner from '../banner/MessageBanner'
import type { FC } from 'react'
import { ERROR_ACCESS_DENIED } from '../constants/LabelConstants'

export interface CreateBannerProps {
  message?: string,
  time?: number,
  callback?: Function,
  collapseProps?: object,
  bannerProps?: BannerProps
}

export type Create403BannerProps = Omit<CreateBannerProps, 'message' | 'bannerProps'>

export type CreateBannerFn = (props: CreateBannerProps) => void
export type ClearBannerFn = () => void
export type Create403BannerFn = (props?: Create403BannerProps) => void
export type APIBannerHandlerFn = (error?: any) => void

export type BannerFns = {
  createBanner: CreateBannerFn
  clearBanner: ClearBannerFn,
  create403Banner: Create403BannerFn,
  apiBannerHandler: APIBannerHandlerFn
}

const MessageContext = createContext<BannerFns>({
  createBanner: noop as CreateBannerFn,
  clearBanner: noop as ClearBannerFn,
  create403Banner: noop as Create403BannerFn,
  apiBannerHandler: noop as APIBannerHandlerFn
})

export const useMessage = () => {
  const { createBanner, clearBanner, create403Banner, apiBannerHandler } = useContext(MessageContext)
  return { createBanner, clearBanner, create403Banner, apiBannerHandler }
}

const MessageProvider: FC<{}> = ({ children }) => {

  const [banner, setBanner] = useState<ReactElement | null>(null)
  const bannerFns = useMemo(() => {

    const createBanner = ({ message = '', time = 5000, callback = () => null, collapseProps = {}, bannerProps = {} }: CreateBannerProps) => {
      const callBack = () => {
        if (callback) {
          callback()
        }
        setBanner(null)
      }
      setBanner(
        <MessageBanner
          {...{
            message,
            timerInMilliSec: time,
            callBack,
            collapseProps,
            bannerProps
          }}
        />
      )
    }

    const create403Banner = (props?: Create403BannerProps) => createBanner({
      message: ERROR_ACCESS_DENIED,
      bannerProps: { appearance: 'warning', showIcon: true },
      ...props
    })

    const clearBanner = () => {
      setBanner(null)
    }
    const apiBannerHandler = (error?: any) => {
      let appearance: BannerAppearance = 'error'
      if (error?.response && error.response.status >= 400 && error.response.status < 500) {
        appearance = 'warning'
      }
      if (error?.response?.status === 403) {
        create403Banner()
      }
      else {
        createBanner({ message: error?.message, bannerProps: { appearance, showIcon: true } })
      }
    }

    return {
      createBanner,
      clearBanner,
      create403Banner,
      apiBannerHandler
    }
  }, [])

  return (
    <MessageContext.Provider value={bannerFns}>
      <div className='tw-relative tw-h-full tw-w-full'>
        <div className='tw-absolute tw-inset-0'>
          {children}
        </div>
        <div className='tw-absolute tw-left-0 tw-right-0 tw-top-0'>
          {banner}
        </div>
      </div>
    </MessageContext.Provider>
  )
}

export default MessageProvider
